import React, { useCallback } from 'react';

import { isNil, isEmpty, isString } from 'lodash';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

import {
  ReferenceInput,
  useRecordContext,
  useUpdate,
  useNotify,
  useRefresh,
  useGetIdentity,
  ReferenceField,
  FunctionField,
  Form,
  RecordContextProvider,
  required,
  AutocompleteInput,
  usePermissions,
} from 'react-admin';

import { useFormContext } from 'react-hook-form';
import { Cancel } from '@mui/icons-material';
import { yellow } from '@mui/material/colors';

import useDialogStatus from '../../../hooks/useDialogStatus';
import hasAccess from '../../../utilities/hasAccess';
import actions from '../../../constants/actions';
import PermissionMissing from '../../utils/PermissionMissing';
import resourceNames from '../../../constants/resourceNames';

const PickMeButton = ({
  source,
  label,
}: {
  source: string;
  label: string;
}) => {
  const { setValue } = useFormContext();
  const { identity } = useGetIdentity();

  const handleAssignMe = useCallback(() => {
    if (identity?.id) {
      setValue(source, identity?.id);
    }
  }, [identity?.id, setValue, source]);

  return (
    <Button onClick={handleAssignMe} size="small">{label}</Button>
  );
};

const UnAssignButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const { open, openDialog, closeDialog } = useDialogStatus();

  const [update] = useUpdate();

  const handleSubmit = useCallback(() => {
    update(
      'cases',
      {
        id: `${record?.id}/unassign`,
        data: {
          unassignFrom: record?.assignment.memberId,
        },
      },
      {
        onSuccess: () => {
          notify('Case assignment have been updated');
          refresh();
          closeDialog();
        },
        onError: () => {
          notify('Could not unassign the case. This might be because the case has already been assigned to someone else.', { type: 'error' });
          refresh();
        },
      },
    );
  }, [update, record?.id, record?.assignment.memberId, notify, refresh, closeDialog]);

  return (
    <>
      <Dialog open={open} maxWidth="sm" fullWidth>
        <Form record={{}} onSubmit={handleSubmit}>
          <DialogTitle fontWeight="bold">
            Unassign case
          </DialogTitle>
          <DialogContent>
            <Typography>
              By continuing, you will unassign
              {' '}
              <RecordContextProvider value={record}>
                <ReferenceField
                  label="Member"
                  source="assignment.memberId"
                  reference={resourceNames.MEMBERS}
                  link={false}
                >
                  <FunctionField
                    render={(member: {firstName: string; lastName: string}) => `${member.firstName} ${member.lastName}`}
                  />
                </ReferenceField>
              </RecordContextProvider>
              {' '}
              from the case. Do you want to continue?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>
              Cancel
            </Button>
            <Button variant="contained" type="submit">
              Unassign
            </Button>
          </DialogActions>
        </Form>
      </Dialog>
      <Tooltip title="Unassign">
        <IconButton
          color="primary"
          onClick={openDialog}
        >
          <PersonRemoveIcon />
        </IconButton>
      </Tooltip>
    </>
  );
};

export const AssignButton = ({
  handleSubmit,
  open,
  openDialog,
  closeDialog,
  filteredOutIds,
  filteredIds,
  isBulkAssign,
}: {
  handleSubmit: ({
    newAssignment,
  }: { newAssignment?: string }) => void;
  open: boolean;
  openDialog: () => void;
  closeDialog: () => void;
  filteredOutIds? : string[];
  filteredIds? : string[];
  isBulkAssign? : boolean;
}) => {
  const excludedText = filteredOutIds ? `The following ${filteredOutIds.length} case(s) are already assigned and will be excluded:` : undefined;
  const assignText = (isBulkAssign && filteredIds) ? `Assign ${filteredIds.length} case(s) ` : 'Assign';
  return (
    <>
      <Dialog open={open} maxWidth="sm" fullWidth>
        <Form record={{}} onSubmit={handleSubmit}>
          <DialogTitle fontWeight="bold">
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h4" fontWeight="bold">Assign case</Typography>
              <IconButton onClick={closeDialog}>
                <Cancel />
              </IconButton>
            </Box>
          </DialogTitle>
          {isBulkAssign && filteredOutIds && filteredOutIds.length > 0 && (
            <DialogContent>
              <Box display="flex" flexDirection="row" padding={2} sx={{ backgroundColor: yellow[100] }}>
                <Box marginX={2}>
                  <ReportProblemOutlinedIcon color="warning" />
                </Box>
                {filteredIds && filteredIds.length > 0 && (
                  <Box marginX={2}>
                    <Typography fontWeight="bold" fontSize={16}>Some cases will not be reassigned</Typography>
                    <Typography>
                      {excludedText}
                    </Typography>
                    {filteredOutIds.map(((filteredOutId) => (
                      <Typography key={filteredOutId}>
                        {'• '}
                        {filteredOutId}
                      </Typography>
                    )
                    ))}
                    <Typography paddingTop={2}>
                      To assign a case that is already assigned, you must unassign it first.
                    </Typography>
                  </Box>
                )}
                {filteredIds && filteredIds.length === 0 && (
                  <Box marginX={2}>
                    <Typography fontWeight="bold" fontSize={16}>No cases will be assigned</Typography>
                    <Typography paddingTop={2}>
                      All selected cases are already assigned.
                      To assign a case that is already assigned, you must unassign it first.
                    </Typography>
                  </Box>
                )}
              </Box>
            </DialogContent>
          )}
          <DialogContent
            sx={{
              alignItems: 'flex-end',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <PickMeButton source="newAssignment" label="Assign me" />
            <ReferenceInput source="newAssignment" reference={resourceNames.MEMBERS} label="Member" filter={{ active: true }}>
              <AutocompleteInput
                fullWidth
                filterSelectedOptions={false}
                filterToQuery={(searchText) => ({ name: searchText })}
                optionText={(item: {firstName: string; lastName: string}) => `${item.firstName} ${item.lastName}`}
                validate={[required()]}
              />
            </ReferenceInput>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              disabled={filteredIds && filteredIds.length === 0}
              variant="contained"
              type="submit"
            >
              {assignText}
            </Button>
          </DialogActions>
        </Form>
      </Dialog>
      {isBulkAssign && (
        <Button
          onClick={openDialog}
          sx={{ paddingX: 1.5, paddingY: 1 }}
        >
          <PersonAddIcon sx={{ marginRight: 1 }} />
          <Typography>Assign</Typography>
        </Button>
      )}
      {!isBulkAssign && (
        <Tooltip title="Assign">
          <IconButton
            color="primary"
            onClick={openDialog}
          >
            <PersonAddIcon />
          </IconButton>
        </Tooltip>
      )}
    </>
  );
};

const CaseAssign = ({
  label,
}: {
  label: string;
}) => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const { open, openDialog, closeDialog } = useDialogStatus();

  const { permissions } = usePermissions();
  const canAssign = hasAccess(permissions, actions.CASE_ASSIGN)
    && hasAccess(permissions, actions.MEMBER_LIST);

  const [update] = useUpdate();
  const handleSubmit = useCallback(({
    newAssignment,
  }: { newAssignment?: string }) => {
    if (!canAssign) return;

    update(
      'cases',
      { id: `${record?.id}/assign`, data: { newAssignment } },
      {
        onSuccess: () => {
          notify('Case assignment have been updated');
          refresh();
          closeDialog();
        },
        onError: () => {
          notify('Could not assign case. This might be because someone else has already assigned the case.', { type: 'error' });
          refresh();
        },
      },
    );
  }, [update, record?.id, notify, refresh, closeDialog, canAssign]);

  return (
    <Box display="flex" flexDirection="row" gap={10} alignItems="center" justifyContent="space-between">
      <Box display="flex" flexDirection="column">
        {hasAccess(permissions, actions.MEMBER_GET) ? (
          <ReferenceField emptyText="-" label={label} source="assignment.memberId" reference={resourceNames.MEMBERS} link="edit">
            <FunctionField
              render={(member: {firstName: string; lastName: string}) => `${member.firstName} ${member.lastName}`}
            />
          </ReferenceField>
        ) : (<PermissionMissing />) }
      </Box>
      <>
        {canAssign && (isNil(record?.assignment?.memberId) || isEmpty(record?.assignment?.memberId))
          && (
            <AssignButton
              handleSubmit={handleSubmit}
              open={open}
              openDialog={openDialog}
              closeDialog={closeDialog}
            />
          )}
        {canAssign && isString(record?.assignment?.memberId) && <UnAssignButton />}
      </>
    </Box>
  );
};

export default CaseAssign;
